import { useMemo } from "react"

import {
  buildPageTransitionCss,
  getPageEnterKeyframes,
  getPageTransitionConfig,
} from "@be-miniapp/ui/utils/page-transition"

interface AnimationStyles {
  initial: React.CSSProperties
  animate: React.CSSProperties
  transition: string
}

interface UseMobileAnimationProps {
  shouldAnimate: boolean
  direction: "forward" | "backward"
}

/**
 * Enter-only page transition fallback for layouts without NativePageTransition.
 *
 * FORWARD (push): new page slides in from the right (x: 30% → 0)
 * BACKWARD (pop): previous page slides in from the left (x: -30% → 0)
 *
 * Transform + subtle opacity, GPU-composited.
 */
export function useMobileAnimation({
  shouldAnimate,
  direction,
}: UseMobileAnimationProps): AnimationStyles {
  return useMemo(() => {
    if (!shouldAnimate) {
      return {
        initial: {},
        animate: {},
        transition: "none",
      }
    }

    const config = getPageTransitionConfig(direction)
    const keyframes = getPageEnterKeyframes(direction)

    return {
      initial: {
        transform: keyframes.from,
        opacity: keyframes.opacityFrom,
      },
      animate: {
        transform: keyframes.to,
        opacity: keyframes.opacityTo,
      },
      transition: buildPageTransitionCss(config),
    }
  }, [shouldAnimate, direction])
}
